/**
 * 카드/갤러리/우승자 탭 공통 레이아웃.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecks } from 'lucide-react';
import { Top3Winners, type WinnerEntry } from './Top3Winners';
import { ResultSectionDashedEmpty } from './ResultSectionEmpty';
import { RESULT_DESIGN_PLACEHOLDER } from '@/constants/resultDesignPlaceholder';
import type { TriagePatientRow, TriageViewMode } from './triageShared';

/** 환자 0명일 때 **실제 `renderCard` 변종**과 맞는 빈 카드 뼈대(KTAS / SALT / 방사선) */
export type TriageResultEmptyLayout = 'ktas' | 'mass_salt' | 'radiation';

const neutralPill =
    'inline-block rounded-full font-semibold text-center justify-self-stretch w-full bg-slate-100 text-slate-500 border border-slate-200/90 px-3 py-1';

function TriageNoPatientCardFrame({ children }: { children: React.ReactNode }): React.ReactElement {
    const box = 'bg-white rounded-xl border border-gray-200/90 shadow-sm';
    return (
        <div className={`${box} p-4 relative min-h-[7.5rem]`}>
            <div className="absolute top-3 end-3 w-6 h-6 rounded-full border border-slate-200/80 bg-slate-50 shrink-0" aria-hidden />
            <div className="font-semibold text-gray-900 mb-2 text-base pe-20">
                <div className="text-sm text-slate-800 font-medium">{RESULT_DESIGN_PLACEHOLDER}</div>
                <div className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-0.5">환자·이름 / 성별·나이</div>
            </div>
            {children}
        </div>
    );
}

function TriageNoPatientGridKtas({ t }: { t: (k: string) => string }): React.ReactElement {
    return (
        <div className="space-y-2 text-sm">
            <div className="grid grid-cols-[auto_minmax(0,1fr)] min-w-0 gap-x-3 gap-y-2 items-center">
                <div className="min-w-9" aria-hidden />
                <div className="font-semibold text-gray-700 text-center text-sm">{t('categoryTriage')}</div>
                <div className="text-gray-500 shrink-0 text-sm">{t('simulationResults.ktasRowCorrect')}</div>
                <span className={neutralPill}>{RESULT_DESIGN_PLACEHOLDER}</span>
                <div className="text-gray-500 shrink-0 text-sm">{t('simulationResults.ktasRowUser')}</div>
                <span className={neutralPill}>{RESULT_DESIGN_PLACEHOLDER}</span>
            </div>
        </div>
    );
}

function TriageNoPatientGridMass({ t }: { t: (k: string) => string }): React.ReactElement {
    return (
        <div className="space-y-2 text-sm">
            <div className="grid grid-cols-[auto_minmax(0,1fr)] min-w-0 gap-x-3 gap-y-2 items-center">
                <div className="min-w-9" aria-hidden />
                <div className="font-semibold text-gray-700 text-center text-sm">{t('categoryTriage')}</div>
                <div className="text-gray-500 shrink-0 text-sm">정답</div>
                <span className={neutralPill}>{RESULT_DESIGN_PLACEHOLDER}</span>
                <div className="text-gray-500 shrink-0 text-sm">답변</div>
                <span className={neutralPill}>{RESULT_DESIGN_PLACEHOLDER}</span>
            </div>
        </div>
    );
}

function TriageNoPatientGridRadiation(): React.ReactElement {
    return (
        <div className="space-y-2 text-sm">
            <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] min-w-0 gap-x-3 gap-y-2 items-center">
                <div className="min-w-9" aria-hidden />
                <div className="font-semibold text-gray-700 text-center text-sm">외상 분류</div>
                <div className="font-semibold text-gray-700 text-center text-sm">복합 분류</div>
                <div className="text-gray-500 shrink-0 text-sm">정답</div>
                <span className={neutralPill}>{RESULT_DESIGN_PLACEHOLDER}</span>
                <span className={neutralPill}>{RESULT_DESIGN_PLACEHOLDER}</span>
                <div className="text-gray-500 shrink-0 text-sm">답변</div>
                <span className={neutralPill}>{RESULT_DESIGN_PLACEHOLDER}</span>
                <span className={neutralPill}>{RESULT_DESIGN_PLACEHOLDER}</span>
            </div>
        </div>
    );
}

export interface TriageResultShellProps {
    title: string;
    patients: TriagePatientRow[];
    viewMode?: TriageViewMode;
    onViewModeChange?: (mode: TriageViewMode) => void;
    isAllParticipantsView?: boolean;
    winners?: WinnerEntry[];
    /** 환자 없음일 때 사용할 뼈대(시나리오: KTAS · SALT/대량 · 방사선). 생략 시 `ktas`. */
    noPatientLayout?: TriageResultEmptyLayout;
    renderCard: (row: TriagePatientRow, idx: number) => React.ReactNode;
    renderEnlarged: (row: TriagePatientRow) => React.ReactNode;
    /** 환자 이름 포맷 함수 (갤러리 버튼용, 번역 등) */
    formatPatientName?: (patientName: string) => string;
}

export function TriageResultShell({
    title,
    patients,
    viewMode = 'card',
    onViewModeChange,
    isAllParticipantsView = false,
    winners = [],
    noPatientLayout = 'ktas',
    renderCard,
    renderEnlarged,
    formatPatientName,
}: TriageResultShellProps): React.ReactElement | null {
    const { t } = useTranslation('class');
    const [selectedIndex, setSelectedIndex] = useState(0);

    const hasPatients = (patients?.length ?? 0) > 0;
    const isGallery = viewMode === 'gallery';
    const isWinners = viewMode === 'winners';
    const showWinnersTab = isAllParticipantsView && winners.length > 0;
    const gridClass = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';
    const selectedPatient = hasPatients ? patients[selectedIndex] : patients[0];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex flex-col gap-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center shrink-0">
                            <ListChecks size={20} className="text-gray-500" />
                        </div>
                        <span className="truncate">{title}</span>
                    </h3>
                </div>
                {onViewModeChange && (
                    <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg border border-gray-200 shrink-0">
                        <button
                            type="button"
                            onClick={() => onViewModeChange('card')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'card' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('simulationResults.viewCard')}
                        </button>
                        <button
                            type="button"
                            onClick={() => onViewModeChange('gallery')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'gallery' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('simulationResults.viewGallery')}
                        </button>
                        {showWinnersTab && (
                            <button
                                type="button"
                                onClick={() => onViewModeChange('winners')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'winners' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                {t('simulationResults.viewWinners')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {isWinners ? (
                winners.length > 0 ? (
                    <Top3Winners winners={winners} />
                ) : (
                    <ResultSectionDashedEmpty message={t('simulationResults.triageNoWinners')} />
                )
            ) : !hasPatients ? (
                <div className={gridClass} role="presentation" aria-label={t('simulationResults.triageNoPatientRows')}>
                    {[0, 1, 2].map((i) => (
                        <TriageNoPatientCardFrame key={`triage-skel-${i}`}>
                            {noPatientLayout === 'mass_salt' ? (
                                <TriageNoPatientGridMass t={t} />
                            ) : noPatientLayout === 'radiation' ? (
                                <TriageNoPatientGridRadiation />
                            ) : (
                                <TriageNoPatientGridKtas t={t} />
                            )}
                        </TriageNoPatientCardFrame>
                    ))}
                </div>
            ) : isGallery ? (
                <>
                    <div className="flex flex-wrap gap-2">
                        {patients.map((row, idx) => {
                            const displayName = formatPatientName ? formatPatientName(row.patientName) : row.patientName;
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => setSelectedIndex(idx)}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedIndex === idx ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                                >
                                    {displayName} ({row.gender ? '남' : '여'}, {row.age}세)
                                </button>
                            );
                        })}
                    </div>
                    {selectedPatient && renderEnlarged(selectedPatient)}
                </>
            ) : (
                <div className={gridClass}>{patients.map((row, idx) => renderCard(row, idx))}</div>
            )}
        </div>
    );
}
